// Consent bridge. The OneTrust CMP is injected by GTM (same container as the main
// site) and exposes its state on `window.OnetrustActiveGroups` (a comma-wrapped list
// like ",C0001,C0002,") and fires `OneTrustGroupsUpdated` whenever consent changes.
// This module normalises that into the two "may we do X?" signals the site needs,
// independent of how OneTrust got onto the page.
//
// The two categories are NOT interchangeable, and the split mirrors the container.
// Web GTM tag 61 ("Consent Mode - OneTrust CMP") maps OneTrust categories onto
// Google Consent Mode like this:
//
//   analytics_storage                            <- C0002
//   ad_storage / ad_user_data / ad_personalization <- C0004
//
// So anything that is an advertising identifier (gclid, fbclid, ttclid and friends)
// belongs behind C0004, not C0002. A visitor may accept analytics and refuse
// advertising, and every Meta / TikTok / StackAdapt tag in the container already
// honours that distinction - the site has to as well.

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    OneTrust?: {
      IsAlertBoxClosed?: () => boolean;
      // Opens the preference centre. The cookie policy's settings button calls
      // it directly rather than relying on OneTrust binding .ot-sdk-show-settings,
      // which it only does when its SDK initialises.
      ToggleInfoDisplay?: () => void;
      // Renders the cookie table into #ot-sdk-cookie-policy. Same reason: on a
      // client-side navigation the container mounts long after SDK init.
      initializeCookiePolicyHtml?: () => void;
      // Switches the CMP's own language. OneTrust picks one at init and keeps
      // it, so a client-side move between /cookiebeleid and /en/cookiebeleid
      // leaves the table and the preference centre in the previous language.
      changeLanguage?: (locale: string) => unknown;
    };
    dataLayer?: unknown[];
  }
}

// OneTrust category for analytics/performance cookies. Override per the container's
// configuration if the analytics tags live in a different group.
const ANALYTICS_GROUP =
  process.env.NEXT_PUBLIC_CONSENT_ANALYTICS_GROUP?.trim() || "C0002";

// OneTrust category for targeting/advertising cookies, i.e. the one the container
// maps onto ad_storage. Anything carrying an ad-platform click id needs this.
const ADVERTISING_GROUP =
  process.env.NEXT_PUBLIC_CONSENT_ADVERTISING_GROUP?.trim() || "C0004";

// Local/dev escape hatch: set NEXT_PUBLIC_CONSENT_FALLBACK=granted to treat consent
// as granted when GTM/OneTrust is not loaded (e.g. local experiment testing).
const FALLBACK_GRANTED =
  process.env.NEXT_PUBLIC_CONSENT_FALLBACK?.trim() === "granted";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const part = document.cookie.split("; ").find((p) => p.startsWith(prefix));
  if (!part || part.length === prefix.length) return null;
  return part.slice(prefix.length);
}

// The answer OneTrust stored on a previous visit, readable before its script
// has run on this one. Its `OptanonConsent` cookie carries the visitor's choice
// per group as `groups=C0001:1,C0002:0,C0004:0` (percent-encoded), and
// `OptanonAlertBoxClosed` marks that the banner was answered at all. GTM, and
// so OneTrust, is deferred to the first interaction, and most visitors leave
// before it loads: without this, a returning visitor who declined last time
// reads as "no answer yet" for the whole of that window, and act-first tracking
// would run for exactly the visitor who said no.
function storedActiveGroups(): string | null {
  const raw = readCookie("OptanonConsent");
  if (!raw) return null;
  let groups: string | null;
  try {
    groups = new URLSearchParams(raw).get("groups");
  } catch {
    return null;
  }
  if (!groups) return null;
  const active = groups
    .split(",")
    .filter((entry) => entry.endsWith(":1"))
    .map((entry) => entry.slice(0, -2));
  return `,${active.join(",")},`;
}

// OnetrustActiveGroups lists only the groups the visitor has ACCEPTED, wrapped in
// commas (",C0001,C0002,"). Match on the wrapped id so a future "C0002A" style
// category cannot satisfy a check for "C0002".
function hasGroup(group: string): boolean {
  if (typeof window === "undefined") return false;
  const live = window.OnetrustActiveGroups;
  const groups =
    typeof live === "string" && live.length > 0 ? live : storedActiveGroups();
  if (groups) {
    return `,${groups},`.replace(/,,+/g, ",").includes(`,${group},`);
  }
  // OneTrust not present yet (or no GTM): fall back only when explicitly allowed.
  return FALLBACK_GRANTED;
}

/**
 * Has the visitor answered the banner at all? Distinct from the two answers
 * below: OneTrust arrives late (GTM is deferred to the first interaction), and
 * until it has loaded and the banner is closed there is no answer, only silence.
 * Treating that silence as a refusal is what deleted a returning visitor's
 * first-touch record on every page view before the CMP had even loaded.
 */
export function hasConsentAnswer(): boolean {
  if (typeof window === "undefined") return false;
  if (FALLBACK_GRANTED) return true;
  // OnetrustActiveGroups alone proves nothing: OneTrust populates it with the
  // default groups (",C0001,") before the visitor has answered anything. The
  // API says whether the banner was closed; its cookie says the same before
  // the script has run, and after it if the API is missing or throws. Absent
  // both, the answer is unknown, never "yes" - a wrong "yes" here re-opens the
  // deletion path this gate exists for.
  const closed = window.OneTrust?.IsAlertBoxClosed;
  if (typeof closed === "function") {
    try {
      return closed() === true;
    } catch {
      // A broken API is not an answer either way; the cookie below still is.
    }
  }
  if (readCookie("OptanonAlertBoxClosed") === null) return false;
  // "Answered" is only worth reporting when the answer itself can be read:
  // from the live groups once the script has run, or from the stored ones
  // before it has. A closed marker with no readable choice would otherwise
  // be scored by hasGroup as a decline, and clear a record over nothing.
  const live = window.OnetrustActiveGroups;
  if (typeof live === "string" && live.length > 0) return true;
  return storedActiveGroups() !== null;
}

/** May we measure this visitor? (PostHog, first-party stats, UTM storage.) */
export function hasAnalyticsConsent(): boolean {
  return hasGroup(ANALYTICS_GROUP);
}

/**
 * May we store ad-platform identifiers? Gates the click ids in the first-touch
 * cookie. Deliberately separate from analytics consent - see the note above.
 */
export function hasAdvertisingConsent(): boolean {
  return hasGroup(ADVERTISING_GROUP);
}

/**
 * Tracking policy, decided 2026-09-02: act first, honour the answer when it
 * arrives. Until the banner is answered nothing is a refusal, so measurement
 * and first-party attribution run from the first render; an explicit decline
 * then clears or strips what was kept. Everything that stores or forwards
 * data reads these two, never the raw answers above.
 */
export function analyticsAllowed(): boolean {
  return !hasConsentAnswer() || hasAnalyticsConsent();
}

export function advertisingAllowed(): boolean {
  return !hasConsentAnswer() || hasAdvertisingConsent();
}

// Subscribe to consent changes. Returns an unsubscribe function. OneTrust dispatches
// `OneTrustGroupsUpdated` on window whenever the banner is answered or the
// preference centre saved; `tvt:consent` is ours, for anything that needs to force
// a re-evaluation (nothing does today, but the CMP gives us no other hook).
export function onConsentChange(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb();
  window.addEventListener("OneTrustGroupsUpdated", handler);
  window.addEventListener("tvt:consent", handler);
  return () => {
    window.removeEventListener("OneTrustGroupsUpdated", handler);
    window.removeEventListener("tvt:consent", handler);
  };
}
